import React, { useEffect, useState } from 'react';
import {
  alpha,
  Avatar,
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import MuiPhoneNumber from 'material-ui-phone-number';
import { id } from 'date-fns/locale';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Cookies from 'js-cookie';
import { getImageFirebase } from '../../utils/getImageFirebase';
import { APICreateDriver, APIUpdateDriver, APIGetOperator } from '../../utils/apiCalled';
import DragAndDropBase64 from '../DragAndDropBase64';
import BackButton from '../backButton';

const generateDriverId = (firstName, lastName) => {
  const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : 'X';
  const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : 'X';

  const now = new Date();
  const hour = now.getHours();
  const minute = now.getMinutes();
  const second = now.getSeconds();

  const toBase = (num) => num.toString(16).toUpperCase();

  const idParts = [
    firstInitial,
    lastInitial,
    toBase(hour),
    toBase(minute),
    toBase(second),
  ];

  // Shuffle the array to randomize the order
  for (let i = idParts.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [idParts[i], idParts[j]] = [idParts[j], idParts[i]];
  }

  // Join the shuffled parts into a single string
  return idParts.join('');
};

export default function PageDriver({
  driver,
  setOpen,
  setSuccMsg,
  setErrMsg,
  setVariant,
  setMainMsg,
  setModalTriggerAPICalled,
  handleCloseMoreDrivers,
  role,
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('');
  const [nricNo, setNricNo] = useState('');
  const [nricImage, setNricImage] = useState('');
  const [driverImage, setDriverImage] = useState('');

  const [loading, setLoading] = useState(false);
  const [posLoading, setPosLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [phoneError, setPhoneError] = useState(false);
  const [phoneErrorMessage, setPhoneErrorMessage] = useState('');
  const [driverImg, setDriverImg] = useState(null);
  const [nricImg, setNricImg] = useState(null);
  const [driverCode, setdriverCode] = useState('');
  const [operatorsList, setOperatorsList] = useState([]);
  const [assignedOperators, setAssignedOperators] = useState([]);
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    if (driver) {
      setFirstName(driver.firstName || '');
      setLastName(driver.lastName || '');
      setEmail(driver.email || '');
      setPhone(driver.phone || '');
      // Set assignedOperators based on role
      if (role === 101) {
        // For super admin (101), use driver's assignedOperators
        if (driver.assignedOperators) {
          setAssignedOperators(Array.isArray(driver.assignedOperators)
            ? driver.assignedOperators
            : [driver.assignedOperators]);
        } else {
          setAssignedOperators([]);
        }
      } else {
        // For non-super admin, use selectedOperatorId from cookies
        const operatorAssigned = Cookies.get('selectedOperatorId');
        if (operatorAssigned) {
          const assignedIds = operatorAssigned.split(',').map((operatorId) => operatorId.trim());
          setAssignedOperators(assignedIds);
        } else {
          setAssignedOperators([]);
        }
      }
      setPassword(driver.password || '');
      setConfirmPassword(driver.password || '');
      setStatus(driver.status || '');
      setNricNo(driver.nricNo || '');
      setdriverCode(driver.driverCode || '');
    }
  }, [driver]);

  const theme = useTheme();

  const fontHeaderStyle = {
    color: theme.palette.grey[700],
    fontWeight: 'fontWeightMedium',
    mr: 5,
    minWidth: '250px',
  };

  const formControlStyle = {
    mb: 3,
    display: 'flex',
    flexDirection: 'row',

  };

  const buttonStyle = {
    border: '1px solid',
    borderColor: 'grey.300',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    color: theme.palette.grey[700],
    backgroundColor: 'background.paper',
    '&:hover': {
      bgcolor: alpha(theme.palette.grey[500], 0.08),
    },
  };

  const confirmButtonStyle = {
    border: 'none',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    bgcolor: theme.palette.primary.main,
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.90),
    },
    color: 'white',
    fontWeight: 'fontWeightMedium',
    '&.Mui-disabled': {
      bgcolor: theme.palette.primary.main,
      color: 'white',
      opacity: '0.6',
    },
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case 'firstName':
        setFirstName(value);
        break;
      case 'lastName':
        setLastName(value);
        break;
      case 'email': {
        setEmail(value);

        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
        if (!emailRegex.test(value)) {
          setEmailError(true);
          setEmailErrorMessage('Please enter a valid email address.');
        } else {
          setEmailError(false);
          setEmailErrorMessage('');
        }
        break;
      }
      case 'phone':
        setPhone(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'confirmPassword':
        setConfirmPassword(value);
        break;
      case 'status':
        setStatus(value);
        break;
      case 'nricNo':
        setNricNo(value);
        break;
      case 'nricImage':
        setNricImage(value);
        break;
      case 'driverImage':
        setDriverImage(value);
        break;
      case 'driverCode':
        setdriverCode(value);
        break;
      case 'operators':
        setAssignedOperators(value);
        break;
      default:
        break;
    }
  };

  const removeAllState = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setPassword('');
    setConfirmPassword('');
    setNricNo('');
    setStatus('');
    setNricImage('');
    setDriverImage('');
    setdriverCode('');
    setAssignedOperators([]);
  };

  const apiAddEditCalled = async (value, driverId) => {
    try {
      if (driver && driver._id) {
        // console.log('UPDATE DRIVER')
        const result = await APIUpdateDriver(setPosLoading, driverId, value);
        if (result.status === 200) {
          setPosLoading(false);
          removeAllState();
          setOpen(true);
          setVariant('success');
          setSuccMsg(`Driver ${value.firstName} ${value.lastName} has sucessfully been edited`);
          setMainMsg('Driver Successfully Edited');
          setModalTriggerAPICalled(true);
        } else {
          setPosLoading(false);
          removeAllState();
          const errorResponse = result.response.data;
          let errorMessage = 'Failed to create driver';
          if (errorResponse && errorResponse.message === 'In use') {
            errorMessage = "This driver's email is already in use";
          } if (errorResponse && errorResponse.message === 'Is invalid') {
            errorMessage = 'Email is invalid';
          }

          setOpen(true);
          setVariant('error');
          setErrMsg(errorMessage); // Set the error message
          setMainMsg('Driver Submission Unsuccessful');
          setModalTriggerAPICalled(true);
        }
      } else {
        const result = await APICreateDriver(setPosLoading, value);
        // console.log('CREATE DRIVER')
        if (result.status === 200) {
          setPosLoading(false);
          removeAllState();
          setOpen(true);
          setVariant('success');
          setSuccMsg(`Driver ${value.firstName} ${value.lastName} has sucessfully been created`);
          setMainMsg('Driver Successfully Created');
          setModalTriggerAPICalled(true);
        } else {
          setPosLoading(false);
          removeAllState();
          const errorResponse = result.response.data;
          let errorMessage = 'Failed to create driver';
          if (errorResponse && errorResponse.message === 'In use') {
            errorMessage = "This driver's email is already in use";
          } if (errorResponse && errorResponse.message === 'Is invalid') {
            errorMessage = 'Email is invalid';
          }

          setOpen(true);
          setVariant('error');
          setErrMsg(errorMessage); // Set the error message
          setMainMsg('Driver Submission Unsuccessful');
          setModalTriggerAPICalled(true);
        }
      }
    } catch (err) {
      setPosLoading(false);
      removeAllState();
      setOpen(true);
      setVariant('error');
      setErrMsg(`Error! ${err.message || 'Please try again'}`);
    }
  };

  const handleSubmit = async () => {
    const validationError = (() => {
      if (!firstName) return 'First Name is required';
      if (!lastName) return 'Last Name is required';
      if (!driverCode) return 'ID Number is required';
      if (!nricNo) return 'NRIC No is required';
      if (!email) return 'Email is required';
      if (!phone) return 'Phone Number is required';
      if (!password) return 'Password is required';
      if (!confirmPassword) return 'Confirm Password is required';
      if (password !== confirmPassword) return 'Passwords do not match';
      if (!status) return 'Status is required';
      // Image checks are only required when creating a new driver
      if (!driver) {
        if (!driverImage) return 'Driver Image is required';
        if (!nricImage) return 'Driver E-KYC Image is required';
      }
      return null; // All checks passed
    })();

    if (validationError) {
      setOpen(true);
      setVariant('error');
      setErrMsg(validationError); // Set the specific error message
      return;
    }
    const payload = {
      firstName,
      lastName,
      email,
      phone,
      password,
      confirmPassword,
      status,
      nricNo,
      driverCode,
      assignedOperators,
    };

    // Always include images for create operations (backend requires them)
    // Frontend validation (lines 330-333) ensures they're not empty before reaching here
    if (!driver) {
      // For create, images are required and validated above
      payload.nricImage = nricImage;
      payload.driverImage = driverImage;
    } else {
      // For update, only include if provided
      if (nricImage) {
        payload.nricImage = nricImage;
      }
      if (driverImage) {
        payload.driverImage = driverImage;
      }
    }

    await apiAddEditCalled(payload, driver ? driver._id : null);
    handleCloseMoreDrivers();
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    if (password && confirmPassword && password !== confirmPassword) {
      setPasswordMatch('Passwords currently do not match');
    } else {
      setPasswordMatch('');
    }
  }, [password, confirmPassword]);

  useEffect(() => {
    const fetchOperators = async () => {
      try {
        const response = await APIGetOperator(setLoading);
        if (response.status === 200) {
          if (role === 101 || role === 900) {
            setOperatorsList(response.data);
          } else {
            const operatorAssigned = Cookies.get('selectedOperatorId');
            const assignedIds = operatorAssigned
              ? operatorAssigned.split(',').map((operatorId) => operatorId.trim())
              : [];
            const matchedOperators = response.data.filter((op) => (
              assignedIds.includes(op._id.toString())
            ));
            setOperatorsList(matchedOperators);
          }
        }
      } catch (error) {
        console.error('Error fetching operators:', error);
      }
    };

    fetchOperators();
  }, []);

  // Prefill assigned operators with selectedOperatorId when
  // creating new driver (only for non-super admins)
  useEffect(() => {
    if (!driver && operatorsList.length > 0 && role !== 101 && role !== 900) {
      const operatorAssigned = Cookies.get('selectedOperatorId');
      if (operatorAssigned) {
        const assignedIds = operatorAssigned.split(',').map((operatorId) => operatorId.trim());
        // Only set if not already set
        if (assignedIds.length > 0 && assignedOperators.length === 0) {
          setAssignedOperators(assignedIds);
        }
      }
    }
  }, [operatorsList, driver, role, assignedOperators.length]);

  const handlePhoneChange = (value) => {
    setPhone(value);

    const digits = value.replace(/\D/g, '');
    if (digits.length < 8 || digits.length > 12) {
      setPhoneError(true);
      setPhoneErrorMessage('Phone number must be between 8 to 12 digits.');
    } else {
      setPhoneError(false);
      setPhoneErrorMessage('');
    }
  };

  const getImageDriver = (location, setState) => {
    getImageFirebase(location)
      .then((img) => {
        setState(img);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    if (driver) {
      // Fetch and set the driver image
      getImageDriver(driver.driverImageURI, setDriverImg);

      // Fetch and set the NRIC image
      getImageDriver(driver.nricImageURI, setNricImg);
    }
  }, [driver]);

  const handleDriverFileUpload = (base64Data) => {
    setDriverImage(base64Data);
    setDriverImg(base64Data);
  };

  const handleNricFileUpload = (base64Data) => {
    setNricImage(base64Data);
    setNricImg(base64Data);
  };

  const handleGenerateId = () => {
    const newId = generateDriverId(firstName, lastName);
    setdriverCode(newId);
  };
  const isFormInvalid = () => {
    if (!driver) {
      const requiredFields = [
        firstName,
        lastName,
        email,
        phone,
        password,
        confirmPassword,
        status,
        nricNo,
        driverCode,
        assignedOperators];

      if (requiredFields.some((field) => !field)
        || password !== confirmPassword
        || !nricImage
        || !driverImage) {
        return true;
      }
    }

    if (driver) {
      const requiredFields = [
        firstName,
        lastName,
        phone,
        password,
        confirmPassword,
        status,
        nricNo,
        driverCode,
        assignedOperators];
      if (requiredFields.some((field) => !field) || password !== confirmPassword) {
        return true;
      }

      const isUnchanged = firstName === driver.firstName
            && lastName === driver.lastName
            && phone === driver.phone
            && password === driver.password
            && confirmPassword === driver.confirmPassword
            && status === driver.status
            && nricNo === driver.nricNo
            && driverCode === driver.driverCode
            && assignedOperators === driver.assignedOperators
            && !nricImage
            && !driverImage;
      if (isUnchanged) { // if is true return true
        // disable button true
        return true;
      }
    }
    // disble button false
    return false;
  };

  return (
    <>
      <Box sx={{ mt: 2 }} />
      <BackButton back={handleCloseMoreDrivers} type="driver" />
      <Box sx={{
        mt: 2, pb: 4, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
      }}
      >
        <Box>
          <Typography variant="h4">{driver ? 'Edit Driver' : 'Create Driver'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'right' }}>
          <Button sx={buttonStyle} onClick={() => handleCloseMoreDrivers()}>
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              Cancel
            </Typography>
          </Button>
          <Button sx={confirmButtonStyle} disabled={isFormInvalid()} onClick={handleSubmit}>
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              Confirm
            </Typography>
          </Button>
        </Box>
      </Box>

      <Box sx={{
        padding: 1, borderTop: '1px solid', borderColor: theme.palette.grey[300], display: 'flex',
      }}
      >
        <Box sx={{ width: '70%', mt: 2 }}>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              First Name
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField label="First Name" name="firstName" value={firstName} onChange={handleInputChange} sx={{ flexGrow: 1 }} />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Last Name
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField label="Last Name" name="lastName" value={lastName} onChange={handleInputChange} sx={{ flexGrow: 1 }} />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              ID Number
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="ID Number"
              disabled={!!driver}
              name="driverCode"
              value={driverCode}
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button onClick={handleGenerateId} disabled={!!driver} variant="contained" size="small" sx={{ mr: -1, textTransform: 'none' }}>
                      Generate
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          <Box sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Assign Operator(s)
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <FormControl fullWidth>
              <Autocomplete
                multiple
                id="assign-operators"
                options={operatorsList}
                disableCloseOnSelect
                disabled={role !== 101 && role !== 900}
                getOptionLabel={(option) => option.operatorName}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                value={
                                Array.isArray(assignedOperators)
                                  ? operatorsList.filter((op) => assignedOperators.includes(op._id))
                                  : []
                            }
                onChange={(event, newValue) => {
                  const newOperatorIds = newValue.map((op) => op._id);
                  setAssignedOperators(newOperatorIds);
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      sx={{ mr: 1 }}
                      checked={selected}
                    />
                    <Typography variant="body2">{option.operatorName}</Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned Operators"
                    placeholder="Select operators"
                    variant="outlined"
                    fullWidth
                  />
                )}
                sx={{
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.paper',
                    borderRadius: 1,
                  },
                  '& .MuiChip-root': {
                    bgcolor: 'primary.light',
                    color: 'white',
                    fontWeight: 500,
                  },
                }}
              />
            </FormControl>
          </Box>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Nric No
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField label="NRIC No" name="nricNo" value={nricNo} onChange={handleInputChange} sx={{ flexGrow: 1 }} />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Email
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField disabled={!!driver} label="Email" name="email" value={email} onChange={handleInputChange} sx={{ flexGrow: 1 }} error={emailError} helperText={emailError ? emailErrorMessage : ''} />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Phone Number
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            {driver ? (
              <TextField disabled={!!driver} label="Phone Number" name="phone" value={phone} onChange={handleInputChange} sx={{ flexGrow: 1 }} />
            ) : (
              <MuiPhoneNumber
                label="Phone Number"
                disabled={!!driver}
                autoFormat={false}
                variant="outlined"
                name="phone"
                defaultCountry="my" // Set default country as needed
                value={phone}
                onChange={handlePhoneChange} // Phone input change handler
                sx={{ flexGrow: 1 }}
                error={phoneError} // Show error style if phone is invalid
                helperText={phoneError ? phoneErrorMessage : ''}
              />
            )}
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Password
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
              value={password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Confirm Password
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
              value={confirmPassword}
              error={!!passwordMatch}
              helperText={passwordMatch}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowConfirmPassword} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          <Box sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Status
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                name="status"
                value={status}
                onChange={handleInputChange}
                sx={{ flexGrow: 1 }}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Driver Image
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', width: '75%', alignItems: 'center',
            }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <a
                  href={driverImg}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Avatar
                    src={driverImg}
                    sx={{
                      height: 150,
                      width: 150,
                      display: 'flex',
                      alignItems: 'center',
                      border: '3px solid',
                      borderColor: theme.palette.grey[200],
                    }}
                  />
                </a>
              </Box>
              <DragAndDropBase64
                onFileUpload={handleDriverFileUpload}
                setOpen={setOpen}
                setVariant={setVariant}
                setErrMsg={setErrMsg}
                setMainMsg={setMainMsg}
              />
            </Box>
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Driver E-KYC Image
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', width: '75%', alignItems: 'center',
            }}
            >
              <a
                href={nricImg}
                rel="noopener noreferrer"
                target="_blank"
              >
                <Avatar
                  src={nricImg}
                  sx={{
                    height: 180,
                    width: 310,
                    borderRadius: '5%',
                    display: 'flex',
                    alignItems: 'center',
                    border: '3px solid',
                    borderColor: theme.palette.grey[200],
                  }}
                />
              </a>
              <DragAndDropBase64 onFileUpload={handleNricFileUpload} />
            </Box>
          </FormControl>
        </Box>
      </Box>
    </>
  );
}
